import moment from 'moment-timezone';

import React from 'react';
import { Column } from 'react-table';

import {
  IEpxOncology,
  IEpxOncologyQOL,
  IEpxOncologyHRSN
} from '../../../../../libs/apps/epxoncology';

import { GetCurrentTZ } from '../../../../../libs/time';

import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  DirectCell,
  YesNoCell,
  StringArrayCell,
  CellFunction,
  NoneGuard,
  RESPONSE_NONE
} from '../../cells';
import Tooltip from '../../../tooltip';

const CURRENT_TZ = GetCurrentTZ();

type Dependencies = [boolean, boolean];

const getDependencies = (sessions: IEpxOncology[]): Dependencies => {
  let shouldAskMeds = false;
  let shouldAskOtherSymptoms = false;
  for (const session of sessions) {
    if (session.shouldAskMeds) {
      shouldAskMeds = true;
    }
    if (session.shouldAskOtherSymptoms) {
      shouldAskOtherSymptoms = true;
    }
  }
  return [shouldAskMeds, shouldAskOtherSymptoms];
};

const getColumns = (sessions: IEpxOncology[]): Column<IEpxOncology>[] => {
  const [shouldAskMeds, shouldAskOtherSymptoms] = getDependencies(sessions);
  // Compose columns based on flags
  const columns: Column<IEpxOncology>[] = [
    {
      Header: 'Symptoms',
      id: 'symptoms',
      Cell: ResponseGuard(
        StringArrayCell(
          'symptoms',
          true,
          'No Symptoms'
        ) as CellFunction<IEpxOncology>
      )
    },
    {
      Header: 'Pain Level',
      id: 'painLevel',
      Cell: ResponseGuard(DirectCell('painLevel') as CellFunction<IEpxOncology>)
    }
  ];
  // Add Med Track column if in use
  if (shouldAskMeds) {
    columns.push({
      Header: 'Trouble with Medications',
      id: 'hasTroubleTakingMeds',
      Cell: ResponseGuard(
        YesNoCell('hasTroubleTakingMeds') as CellFunction<IEpxOncology>
      )
    });
  }
  // Add Other Symptoms column if in use
  if (shouldAskOtherSymptoms) {
    columns.push({
      Header: 'Other Symptoms',
      id: 'hasOtherSymptoms',
      Cell: ResponseGuard(
        YesNoCell('hasOtherSymptoms') as CellFunction<IEpxOncology>
      )
    });
  }
  return columns;
};

// Weekly Assessment Questions
const EpxOncologyTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxOncology>
      url="/epxoncology/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      {...props}
    />
  );
};

export default EpxOncologyTable;

// QOL

const PHQ2 = [
  'Over the past 2 weeks, have you had little interest or pleasure in doing things?\nRate from 0 (not at all) to 3 (almost daily).',
  'Over the past 2 weeks, how often have you felt down, depressed, or hopeless?\nRate from 0 (not at all) to 3 (almost daily).'
];

const PHQq1Cell: CellFunction<IEpxOncologyQOL> = ({
  row: { original: session }
}) => {
  const answer = session.phq2Q1;
  return (
    NoneGuard(answer) || (
      <Tooltip content={PHQ2[0]}>
        <span>{answer}</span>
      </Tooltip>
    )
  );
};

const PHQq2Cell: CellFunction<IEpxOncologyQOL> = ({
  row: { original: session }
}) => {
  const answer = session.phq2Q2;
  return (
    NoneGuard(answer) || (
      <Tooltip content={PHQ2[1]}>
        <span>{answer}</span>
      </Tooltip>
    )
  );
};

export const EpxOncologyQOLTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxOncologyQOL>[] = [
    {
      Header: 'PHQ2 Q1',
      id: 'phq2Q1',
      Cell: ResponseGuard(PHQq1Cell)
    },
    {
      Header: 'PHQ2 Q2',
      id: 'phq2Q2',
      Cell: ResponseGuard(PHQq2Cell)
    },
    {
      Header: 'Responded At',
      accessor: 'respondedAt',
      Cell: ({ cell: { value }, row: { original: session } }) => {
        if (!value) {
          return RESPONSE_NONE;
        }
        const tz = session?.schedule?.tz || CURRENT_TZ;
        const time = moment.tz(value as Date, tz);
        return <span>{time.format('MM/DD hh:mm A')}</span>;
      }
    },
    {
      Header: 'Msg Time',
      accessor: 'time',
      Cell: ({ cell: { value }, row: { original: session } }) => {
        const tz = session?.schedule?.tz || CURRENT_TZ;
        const time = moment.tz(value as Date, tz);
        return <span>{time.format('MM/DD/YY hh:mm A')}</span>;
      }
    }
  ];
  return (
    <SessionTable<IEpxOncologyQOL>
      url="/epxoncology/data_qol/"
      disableDefaultColumns
      columns={columns}
      {...props}
    />
  );
};

// HRSN

export const EpxOncologyHRSNTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxOncologyHRSN>[] = [
    {
      Header: 'Needs',
      id: 'needs',
      Cell: ResponseGuard(
        StringArrayCell(
          'needs',
          true,
          'No Needs'
        ) as CellFunction<IEpxOncologyHRSN>
      )
    },
    {
      Header: 'Responded At',
      accessor: 'respondedAt',
      Cell: ({ cell: { value }, row: { original: session } }) => {
        if (!value) {
          return RESPONSE_NONE;
        }
        const tz = session?.schedule?.tz || CURRENT_TZ;
        const time = moment.tz(value as Date, tz);
        return <span>{time.format('MM/DD hh:mm A')}</span>;
      }
    },
    {
      Header: 'Msg Time',
      accessor: 'time',
      Cell: ({ cell: { value }, row: { original: session } }) => {
        const tz = session?.schedule?.tz || CURRENT_TZ;
        const time = moment.tz(value as Date, tz);
        return <span>{time.format('MM/DD/YY hh:mm A')}</span>;
      }
    }
  ];
  return (
    <SessionTable<IEpxOncologyHRSN>
      url="/epxoncology/data_hrsn/"
      disableDefaultColumns
      columns={columns}
      {...props}
    />
  );
};
